export const API_BASE = process.env.REACT_APP_PUBLIC_API_URL || "https://rest.blutec.ai";

export const API_CATEGORIES = [
  {
    id: "getting-started",
    title: "Getting Started",
    icon: "Rocket",
    items: [{ id: "introduction", title: "Introduction" }],
  },
  {
    id: "authentication",
    title: "Authentication",
    icon: "Shield",
    items: [{ id: "api-key-usage", title: "API Key Usage" }],
  },
  {
    id: "messaging",
    title: "Messaging",
    icon: "MessageSquare",
    items: [
      { id: "send-text", title: "Send Text Message" },
      { id: "send-media", title: "Send Media Message" },
      { id: "send-template", title: "Send Template Message" },
    ],
  },
  {
    id: "phones",
    title: "Phone Numbers",
    icon: "Phone",
    items: [{ id: "list-phones", title: "List Phone Numbers" }],
  },
  {
    id: "templates",
    title: "Templates",
    icon: "FileText",
    items: [
      { id: "list-templates", title: "List Templates" },
      { id: "template-guide", title: "Template Sending Guide" },
    ],
  },
  {
    id: "media",
    title: "Media",
    icon: "Image",
    items: [{ id: "upload-media", title: "Upload Media" }],
  },
  {
    id: "wallet",
    title: "Wallet",
    icon: "Wallet",
    items: [
      { id: "get-wallet", title: "Get Wallet" },
      { id: "get-wallet-prices", title: "Get Prices" },
    ],
  },
  {
    id: "flows",
    title: "Flow Activation",
    icon: "Zap",
    items: [
      { id: "list-flows", title: "List Flows" },
      { id: "get-flow-variables", title: "Get Expected Variables" },
      { id: "validate-flow-activation", title: "Validate Activation" },
      { id: "activate-flow", title: "Activate Flow" },
    ],
  },
  {
    id: "errors",
    title: "Error Reference",
    icon: "AlertTriangle",
    items: [{ id: "error-codes", title: "Error Codes" }],
  },
];

export const ENDPOINTS = {
  "send-text": {
    id: "send-text",
    title: "Send Text Message",
    description: "Send a plain text WhatsApp message to a recipient. Session text is billed as service (usually ₹0). The response includes remaining wallet balance.",
    method: "POST",
    path: "/api/v1/messages/text",
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
      "Content-Type": "application/json",
    },
    requestBody: {
      phone_number: "919876543210",
      to: "919876543211",
      message: "Hello from Ping API!",
    },
    requestFields: [
      { name: "phone_number", type: "string", required: true, description: "Your WhatsApp Business phone number (or use phone_id instead)" },
      { name: "phone_id", type: "string", required: false, description: "Meta phone number ID (alternative to phone_number)" },
      { name: "to", type: "string", required: true, description: "Recipient phone number with country code" },
      { name: "message", type: "string", required: true, description: "Text message content" },
    ],
    responseBody: {
      success: true,
      data: {
        message_id: "wamid.HBgMOTE5ODc2NTQzMjEwFQIAERgSMEI3RjY4MzVGNzM4Q0Y3AA==",
        status: "accepted",
        wallet: {
          charged: 0,
          remaining: 124.15,
          currency: "INR",
          category: "service",
          status: "active",
        },
      },
    },
    codeExamples: {
      curl: `curl -X POST ${API_BASE}/api/v1/messages/text \\
  -H "X-API-Key: pk_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "phone_number": "919876543210",
    "to": "919876543211",
    "message": "Hello from Ping API!"
  }'`,
      javascript: `const response = await fetch("${API_BASE}/api/v1/messages/text", {
  method: "POST",
  headers: {
    "X-API-Key": "pk_live_your_api_key_here",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    phone_number: "919876543210",
    to: "919876543211",
    message: "Hello from Ping API!",
  }),
});

const data = await response.json();
console.log(data);`,
      python: `import requests

response = requests.post(
    "${API_BASE}/api/v1/messages/text",
    headers={
        "X-API-Key": "pk_live_your_api_key_here",
        "Content-Type": "application/json",
    },
    json={
        "phone_number": "919876543210",
        "to": "919876543211",
        "message": "Hello from Ping API!",
    },
)

print(response.json())`,
      nodejs: `const axios = require("axios");

const { data } = await axios.post(
  "${API_BASE}/api/v1/messages/text",
  {
    phone_number: "919876543210",
    to: "919876543211",
    message: "Hello from Ping API!",
  },
  {
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
    },
  }
);

console.log(data);`,
    },
  },

  "send-media": {
    id: "send-media",
    title: "Send Media Message",
    description: "Send a media message (image, video, audio, or document) to a recipient. You can provide either a public media URL or a previously uploaded media_id. Session media is billed as service (usually ₹0).",
    method: "POST",
    path: "/api/v1/messages/media",
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
      "Content-Type": "application/json",
    },
    requestBody: {
      phone_number: "919876543210",
      to: "919876543211",
      type: "image",
      media_url: "https://example.com/image.jpg",
      caption: "Product Image",
    },
    requestFields: [
      { name: "phone_number", type: "string", required: true, description: "Your WhatsApp Business phone number (or use phone_id)" },
      { name: "to", type: "string", required: true, description: "Recipient phone number with country code" },
      { name: "type", type: "string", required: true, description: "Media type: image, video, audio, or document" },
      { name: "media_url", type: "string", required: false, description: "Public URL of the media file (use this or media_id)" },
      { name: "media_id", type: "string", required: false, description: "Meta media ID from a previous upload (use this or media_url)" },
      { name: "caption", type: "string", required: false, description: "Caption text for the media (image, video, document)" },
      { name: "filename", type: "string", required: false, description: "Filename for document type media" },
    ],
    responseBody: {
      success: true,
      data: {
        message_id: "wamid.HBgMOTE5ODc2NTQzMjEwFQIAERgSMEI3RjY4MzVGNzM4Q0Y3AA==",
        status: "accepted",
        wallet: {
          charged: 0,
          remaining: 124.15,
          currency: "INR",
          category: "service",
          status: "active",
        },
      },
    },
    codeExamples: {
      curl: `curl -X POST ${API_BASE}/api/v1/messages/media \\
  -H "X-API-Key: pk_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "phone_number": "919876543210",
    "to": "919876543211",
    "type": "image",
    "media_url": "https://example.com/image.jpg",
    "caption": "Product Image"
  }'`,
      javascript: `const response = await fetch("${API_BASE}/api/v1/messages/media", {
  method: "POST",
  headers: {
    "X-API-Key": "pk_live_your_api_key_here",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    phone_number: "919876543210",
    to: "919876543211",
    type: "image",
    media_url: "https://example.com/image.jpg",
    caption: "Product Image",
  }),
});

const data = await response.json();
console.log(data);`,
      python: `import requests

response = requests.post(
    "${API_BASE}/api/v1/messages/media",
    headers={
        "X-API-Key": "pk_live_your_api_key_here",
        "Content-Type": "application/json",
    },
    json={
        "phone_number": "919876543210",
        "to": "919876543211",
        "type": "image",
        "media_url": "https://example.com/image.jpg",
        "caption": "Product Image",
    },
)

print(response.json())`,
      nodejs: `const axios = require("axios");

const { data } = await axios.post(
  "${API_BASE}/api/v1/messages/media",
  {
    phone_number: "919876543210",
    to: "919876543211",
    type: "image",
    media_url: "https://example.com/image.jpg",
    caption: "Product Image",
  },
  {
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
    },
  }
);

console.log(data);`,
    },
  },

  "send-template": {
    id: "send-template",
    title: "Send Template Message",
    description: "Send a pre-approved WhatsApp template message to a recipient. Templates must be approved by Meta. Use GET /templates to get the real name and language, then GET /wallet to confirm balance. Marketing templates debit the prepaid INR wallet; insufficient funds return 402.",
    method: "POST",
    path: "/api/v1/messages/template",
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
      "Content-Type": "application/json",
    },
    requestBody: {
      phone_number: "919876543210",
      to: "919876543211",
      template_name: "order_confirmation",
      language: "en_US",
      components: [
        {
          type: "body",
          parameters: [{ type: "text", text: "John" }, { type: "text", text: "ORD12345" }],
        },
      ],
    },
    requestFields: [
      { name: "phone_number", type: "string", required: true, description: "Your WhatsApp Business phone number (or use phone_id)" },
      { name: "to", type: "string", required: true, description: "Recipient phone number with country code" },
      { name: "template_name", type: "string", required: true, description: "Name of the approved template" },
      { name: "language", type: "string", required: false, description: "Template language code (default: en_US)" },
      { name: "components", type: "array", required: false, description: "Template components with parameter values" },
    ],
    responseBody: {
      success: true,
      data: {
        message_id: "wamid.HBgMOTE5ODc2NTQzMjEwFQIAERgSMEI3RjY4MzVGNzM4Q0Y3AA==",
        status: "accepted",
        wallet: {
          charged: 0.85,
          remaining: 123.3,
          currency: "INR",
          category: "marketing_lite",
          status: "active",
        },
      },
    },
    codeExamples: {
      curl: `curl -X POST ${API_BASE}/api/v1/messages/template \\
  -H "X-API-Key: pk_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "phone_number": "919876543210",
    "to": "919876543211",
    "template_name": "order_confirmation",
    "language": "en_US",
    "components": [
      {
        "type": "body",
        "parameters": [
          { "type": "text", "text": "John" },
          { "type": "text", "text": "ORD12345" }
        ]
      }
    ]
  }'`,
      javascript: `const response = await fetch("${API_BASE}/api/v1/messages/template", {
  method: "POST",
  headers: {
    "X-API-Key": "pk_live_your_api_key_here",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    phone_number: "919876543210",
    to: "919876543211",
    template_name: "order_confirmation",
    language: "en_US",
    components: [
      {
        type: "body",
        parameters: [
          { type: "text", text: "John" },
          { type: "text", text: "ORD12345" },
        ],
      },
    ],
  }),
});

const data = await response.json();
console.log(data);`,
      python: `import requests

response = requests.post(
    "${API_BASE}/api/v1/messages/template",
    headers={
        "X-API-Key": "pk_live_your_api_key_here",
        "Content-Type": "application/json",
    },
    json={
        "phone_number": "919876543210",
        "to": "919876543211",
        "template_name": "order_confirmation",
        "language": "en_US",
        "components": [
            {
                "type": "body",
                "parameters": [
                    {"type": "text", "text": "John"},
                    {"type": "text", "text": "ORD12345"},
                ],
            }
        ],
    },
)

print(response.json())`,
      nodejs: `const axios = require("axios");

const { data } = await axios.post(
  "${API_BASE}/api/v1/messages/template",
  {
    phone_number: "919876543210",
    to: "919876543211",
    template_name: "order_confirmation",
    language: "en_US",
    components: [
      {
        type: "body",
        parameters: [
          { type: "text", text: "John" },
          { type: "text", text: "ORD12345" },
        ],
      },
    ],
  },
  {
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
    },
  }
);

console.log(data);`,
    },
  },

  "list-phones": {
    id: "list-phones",
    title: "List Phone Numbers",
    description: "List WhatsApp business numbers connected to this API key. Use phone_number when sending messages and when filtering templates. Tokens are never returned.",
    method: "GET",
    path: "/api/v1/phones",
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
    },
    responseBody: {
      success: true,
      data: {
        phones: [
          {
            phone_id: "123456789012345",
            phone_number: "919876543210",
            verified_name: "Lokesh Pareek",
            waba_id: "109876543210987",
            status: "live",
          },
        ],
      },
    },
    codeExamples: {
      curl: `curl -X GET ${API_BASE}/api/v1/phones \\
  -H "X-API-Key: pk_live_your_api_key_here"`,
      javascript: `const response = await fetch("${API_BASE}/api/v1/phones", {
  headers: { "X-API-Key": "pk_live_your_api_key_here" },
});
const data = await response.json();
console.log(data.data.phones);`,
      python: `import requests

response = requests.get(
    "${API_BASE}/api/v1/phones",
    headers={"X-API-Key": "pk_live_your_api_key_here"},
)
print(response.json())`,
      nodejs: `const axios = require("axios");

const { data } = await axios.get("${API_BASE}/api/v1/phones", {
  headers: { "X-API-Key": "pk_live_your_api_key_here" },
});
console.log(data.data.phones);`,
    },
  },

  "list-templates": {
    id: "list-templates",
    title: "List Templates",
    description: "Retrieve WhatsApp message templates for this API key. Filter by status, category, name, or a connected phone_number (that number's WABA only). Each template includes body preview text, footer, buttons, header_media when a media URL is required at send time, header_example when the approved sample is a public image, video, or document URL, and a variables list for header, body, and button slots.",
    method: "GET",
    path: "/api/v1/templates",
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
    },
    queryParams: [
      { name: "status", type: "string", required: false, description: "Filter by status (e.g., APPROVED, PENDING, REJECTED)" },
      { name: "category", type: "string", required: false, description: "Filter by category (MARKETING, UTILITY, AUTHENTICATION)" },
      { name: "search", type: "string", required: false, description: "Search templates by name" },
      { name: "phone_number", type: "string", required: false, description: "Connected WhatsApp business number. Limits results to that number's WABA. Unknown numbers return 404." },
      { name: "phone_id", type: "string", required: false, description: "Meta phone number ID. Alternative to phone_number." },
      { name: "limit", type: "number", required: false, description: "Number of results (default: 50, max: 500)" },
      { name: "offset", type: "number", required: false, description: "Pagination offset (default: 0)" },
    ],
    responseBody: {
      success: true,
      data: {
        templates: [
          {
            id: 1,
            template_id: "123456789",
            name: "order_confirmation",
            language: "en_US",
            category: "UTILITY",
            parameter_format: "numbered",
            status: "APPROVED",
            quality_score: "GREEN",
            body: "Hello {{1}}, your order {{2}} is confirmed.",
            header_text: null,
            header_media: "image",
            header_example: "https://cdn.example.com/header.png",
            footer: "Reply STOP to opt out",
            buttons: [{ type: "URL", text: "Join" }],
            variables: [
              { component: "body", index: 1, name: null, example: "Asha" },
              { component: "body", index: 2, name: null, example: "ORD12345" },
            ],
            created_at: "2026-01-15T10:30:00.000Z",
            updated_at: "2026-01-15T10:30:00.000Z",
          },
        ],
        count: 1,
        limit: 50,
        offset: 0,
        phone_number: "919876543210",
        waba_id: "109876543210987",
      },
    },
    codeExamples: {
      curl: `curl -X GET "${API_BASE}/api/v1/templates?status=APPROVED&phone_number=919876543210&limit=10" \\
  -H "X-API-Key: pk_live_your_api_key_here"`,
      javascript: `const response = await fetch(
  "${API_BASE}/api/v1/templates?status=APPROVED&phone_number=919876543210&limit=10",
  {
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
    },
  }
);

const data = await response.json();
console.log(data);`,
      python: `import requests

response = requests.get(
    "${API_BASE}/api/v1/templates",
    headers={
        "X-API-Key": "pk_live_your_api_key_here",
    },
    params={
        "status": "APPROVED",
        "phone_number": "919876543210",
        "limit": 10,
    },
)

print(response.json())`,
      nodejs: `const axios = require("axios");

const { data } = await axios.get(
  "${API_BASE}/api/v1/templates",
  {
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
    },
    params: {
      status: "APPROVED",
      phone_number: "919876543210",
      limit: 10,
    },
  }
);

console.log(data);`,
    },
  },

  "upload-media": {
    id: "upload-media",
    title: "Upload Media",
    description: "Upload a media file (image, video, audio, document) to get a reusable media_id. The file is stored securely and registered with Meta WhatsApp Cloud API. Use the returned media_id in send media message requests.",
    method: "POST",
    path: "/api/v1/media/upload",
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
      "Content-Type": "multipart/form-data",
    },
    isMultipart: true,
    requestFields: [
      { name: "phone_number", type: "string", required: true, description: "Your WhatsApp Business phone number (form field)" },
      { name: "file", type: "file", required: true, description: "Media file to upload (max 100MB)" },
    ],
    responseBody: {
      success: true,
      data: {
        media_id: "1234567890",
        url: "https://object.sakshamcloud.com/speraza-marketplace-bucket/blutec-ping/api-media/1/image_1741345678901.jpg",
      },
    },
    codeExamples: {
      curl: `curl -X POST ${API_BASE}/api/v1/media/upload \\
  -H "X-API-Key: pk_live_your_api_key_here" \\
  -F "phone_number=919876543210" \\
  -F "file=@/path/to/image.jpg"`,
      javascript: `const formData = new FormData();
formData.append("phone_number", "919876543210");
formData.append("file", fileInput.files[0]);

const response = await fetch("${API_BASE}/api/v1/media/upload", {
  method: "POST",
  headers: {
    "X-API-Key": "pk_live_your_api_key_here",
  },
  body: formData,
});

const data = await response.json();
console.log(data.data.media_id);`,
      python: `import requests

with open("/path/to/image.jpg", "rb") as f:
    response = requests.post(
        "${API_BASE}/api/v1/media/upload",
        headers={
            "X-API-Key": "pk_live_your_api_key_here",
        },
        data={"phone_number": "919876543210"},
        files={"file": f},
    )

print(response.json())`,
      nodejs: `const axios = require("axios");
const FormData = require("form-data");
const fs = require("fs");

const form = new FormData();
form.append("phone_number", "919876543210");
form.append("file", fs.createReadStream("/path/to/image.jpg"));

const { data } = await axios.post(
  "${API_BASE}/api/v1/media/upload",
  form,
  {
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
      ...form.getHeaders(),
    },
  }
);

console.log(data.data.media_id);`,
    },
  },

  "get-wallet": {
    id: "get-wallet",
    title: "Get Wallet",
    description: "Return the prepaid INR wallet for this API key: remaining balance and status. Recharge from the Ping dashboard. Frozen wallets cannot send messages.",
    method: "GET",
    path: "/api/v1/wallet",
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
    },
    responseBody: {
      success: true,
      data: {
        balance: 124.15,
        currency: "INR",
        status: "active",
      },
    },
    codeExamples: {
      curl: `curl -X GET ${API_BASE}/api/v1/wallet \\
  -H "X-API-Key: pk_live_your_api_key_here"`,
      javascript: `const response = await fetch("${API_BASE}/api/v1/wallet", {
  headers: {
    "X-API-Key": "pk_live_your_api_key_here",
  },
});

const data = await response.json();
console.log(data);`,
      python: `import requests

response = requests.get(
    "${API_BASE}/api/v1/wallet",
    headers={
        "X-API-Key": "pk_live_your_api_key_here",
    },
)

print(response.json())`,
      nodejs: `const axios = require("axios");

const { data } = await axios.get(
  "${API_BASE}/api/v1/wallet",
  {
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
    },
  }
);

console.log(data);`,
    },
  },

  "get-wallet-prices": {
    id: "get-wallet-prices",
    title: "Get Prices",
    description: "Return the per-message prices charged to this account for a destination country. Use these to check whether the wallet can cover a template before sending. Session text/media use the service category (usually ₹0).",
    method: "GET",
    path: "/api/v1/wallet/prices",
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
    },
    queryParams: [
      { name: "country", type: "string", required: false, description: "ISO 3166-1 alpha-2 destination country (default: IN)" },
    ],
    responseBody: {
      success: true,
      data: {
        country: "IN",
        currency: "INR",
        prices: {
          marketing: 0.95,
          marketing_lite: 0.85,
          utility: 0.125,
          authentication: 0.14,
          service: 0,
        },
      },
    },
    codeExamples: {
      curl: `curl -X GET "${API_BASE}/api/v1/wallet/prices?country=IN" \\
  -H "X-API-Key: pk_live_your_api_key_here"`,
      javascript: `const response = await fetch("${API_BASE}/api/v1/wallet/prices?country=IN", {
  headers: {
    "X-API-Key": "pk_live_your_api_key_here",
  },
});

const data = await response.json();
console.log(data);`,
      python: `import requests

response = requests.get(
    "${API_BASE}/api/v1/wallet/prices",
    headers={
        "X-API-Key": "pk_live_your_api_key_here",
    },
    params={
        "country": "IN",
    },
)

print(response.json())`,
      nodejs: `const axios = require("axios");

const { data } = await axios.get(
  "${API_BASE}/api/v1/wallet/prices",
  {
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
    },
    params: { country: "IN" },
  }
);

console.log(data);`,
    },
  },

  "list-flows": {
    id: "list-flows",
    title: "List Flows",
    description: "Returns a lightweight list of active chatbot flows with expected API variables (required and optional) loaded from each flow's trigger node.",
    method: "GET",
    path: "/api/v1/flows",
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
    },
    queryParams: [
      { name: "phone_number", type: "string", required: false, description: "Filter to flows allocated to this business phone number" },
      { name: "phone_id", type: "string", required: false, description: "Filter by Meta phone number ID (alternative to phone_number)" },
      { name: "api_only", type: "boolean", required: false, description: "When true, returns only flows with API trigger type" },
    ],
    responseBody: {
      success: true,
      data: {
        flows: [
          {
            id: 12,
            name: "Order Follow-up",
            description: "Starts order status collection for a customer",
            trigger_type: "api",
            is_active: true,
            expected_variables: {
              required: [{ name: "order_id", description: "Customer order ID" }],
              optional: [{ name: "customer_name", description: "Display name override" }],
            },
          },
        ],
      },
    },
    codeExamples: {
      curl: `curl -X GET "${API_BASE}/api/v1/flows?api_only=true&phone_number=919876543210" \\
  -H "X-API-Key: pk_live_your_api_key_here"`,
      javascript: `const response = await fetch("${API_BASE}/api/v1/flows?api_only=true&phone_number=919876543210", {
  headers: { "X-API-Key": "pk_live_your_api_key_here" },
});
const data = await response.json();
console.log(data.data.flows);`,
      python: `import requests

response = requests.get(
    "${API_BASE}/api/v1/flows",
    headers={"X-API-Key": "pk_live_your_api_key_here"},
    params={"api_only": "true", "phone_number": "919876543210"},
)
print(response.json())`,
      nodejs: `const axios = require("axios");

const { data } = await axios.get("${API_BASE}/api/v1/flows", {
  headers: { "X-API-Key": "pk_live_your_api_key_here" },
  params: { api_only: true, phone_number: "919876543210" },
});
console.log(data.data.flows);`,
    },
  },

  "get-flow-variables": {
    id: "get-flow-variables",
    title: "Get Expected Variables",
    description: "Returns the required and optional variables configured on the flow's API trigger node. Call this before activation to know which variables must be supplied.",
    method: "GET",
    path: "/api/v1/flows/{flowId}/variables",
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
    },
    pathParams: [
      { name: "flowId", type: "integer", required: true, description: "Flow ID from the list flows endpoint" },
    ],
    responseBody: {
      success: true,
      data: {
        flow_id: 12,
        flow_name: "Order Follow-up",
        trigger_type: "api",
        variables: {
          required: [{ name: "order_id", description: "Customer order ID" }],
          optional: [{ name: "customer_name", description: "Display name override" }],
        },
        expected_variables: {
          required: [{ name: "order_id", description: "Customer order ID" }],
          optional: [{ name: "customer_name", description: "Display name override" }],
        },
      },
    },
    codeExamples: {
      curl: `curl -X GET "${API_BASE}/api/v1/flows/12/variables" \\
  -H "X-API-Key: pk_live_your_api_key_here"`,
      javascript: `const response = await fetch("${API_BASE}/api/v1/flows/12/variables", {
  headers: { "X-API-Key": "pk_live_your_api_key_here" },
});
console.log(await response.json());`,
      python: `import requests

response = requests.get(
    "${API_BASE}/api/v1/flows/12/variables",
    headers={"X-API-Key": "pk_live_your_api_key_here"},
)
print(response.json())`,
      nodejs: `const axios = require("axios");

const { data } = await axios.get("${API_BASE}/api/v1/flows/12/variables", {
  headers: { "X-API-Key": "pk_live_your_api_key_here" },
});
console.log(data);`,
    },
  },

  "validate-flow-activation": {
    id: "validate-flow-activation",
    title: "Validate Activation",
    description: "Validates a flow activation request without starting the flow. Checks flow status, phone allocation, required variables, and variable names.",
    method: "POST",
    path: "/api/v1/flows/{flowId}/validate",
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
      "Content-Type": "application/json",
    },
    pathParams: [
      { name: "flowId", type: "integer", required: true, description: "Flow ID to validate" },
    ],
    requestBody: {
      phone_number: "919876543210",
      to: "919876543211",
      variables: {
        order_id: "ORD-1001",
        customer_name: "Rahul",
      },
    },
    requestFields: [
      { name: "phone_number", type: "string", required: true, description: "Your WhatsApp Business phone number (or use phone_id)" },
      { name: "phone_id", type: "string", required: false, description: "Meta phone number ID" },
      { name: "to", type: "string", required: true, description: "Customer WhatsApp number to activate the flow for" },
      { name: "variables", type: "object", required: false, description: "Key-value map of pre-filled flow variables" },
    ],
    responseBody: {
      success: true,
      data: {
        validated: true,
        dry_run: true,
        flow_id: 12,
        flow_name: "Order Follow-up",
        customer_phone: "919876543211",
        variables: { order_id: "ORD-1001", customer_name: "Rahul" },
      },
    },
    codeExamples: {
      curl: `curl -X POST ${API_BASE}/api/v1/flows/12/validate \\
  -H "X-API-Key: pk_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "phone_number": "919876543210",
    "to": "919876543211",
    "variables": { "order_id": "ORD-1001" }
  }'`,
      javascript: `const response = await fetch("${API_BASE}/api/v1/flows/12/validate", {
  method: "POST",
  headers: {
    "X-API-Key": "pk_live_your_api_key_here",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    phone_number: "919876543210",
    to: "919876543211",
    variables: { order_id: "ORD-1001" },
  }),
});
console.log(await response.json());`,
      python: `import requests

response = requests.post(
    "${API_BASE}/api/v1/flows/12/validate",
    headers={
        "X-API-Key": "pk_live_your_api_key_here",
        "Content-Type": "application/json",
    },
    json={
        "phone_number": "919876543210",
        "to": "919876543211",
        "variables": {"order_id": "ORD-1001"},
    },
)
print(response.json())`,
      nodejs: `const axios = require("axios");

const { data } = await axios.post(
  "${API_BASE}/api/v1/flows/12/validate",
  {
    phone_number: "919876543210",
    to: "919876543211",
    variables: { order_id: "ORD-1001" },
  },
  { headers: { "X-API-Key": "pk_live_your_api_key_here" } }
);
console.log(data);`,
    },
  },

  "activate-flow": {
    id: "activate-flow",
    title: "Activate Flow",
    description: "Activates an API-triggered chatbot flow for a specific customer phone number with pre-filled variables. Returns immediately after validation and session reset; flow orchestration (messages, nodes, waits) continues in the background. Any existing flow session for that customer on your business phone is automatically ended first — including DB state, execution variables, and Redis cache.",
    method: "POST",
    path: "/api/v1/flows/{flowId}/activate",
    headers: {
      "X-API-Key": "pk_live_your_api_key_here",
      "Content-Type": "application/json",
    },
    pathParams: [
      { name: "flowId", type: "integer", required: true, description: "Flow ID to activate" },
    ],
    requestBody: {
      phone_number: "919876543210",
      to: "919876543211",
      variables: {
        order_id: "ORD-1001",
        customer_name: "Rahul",
      },
    },
    requestFields: [
      { name: "phone_number", type: "string", required: true, description: "Your WhatsApp Business phone number (or use phone_id)" },
      { name: "phone_id", type: "string", required: false, description: "Meta phone number ID" },
      { name: "to", type: "string", required: true, description: "Customer WhatsApp number to activate the flow for" },
      { name: "variables", type: "object", required: false, description: "Pre-filled flow variables (all required variables must be included)" },
      { name: "dry_run", type: "boolean", required: false, description: "Validate only without starting the flow (default: false)" },
    ],
    responseBody: {
      success: true,
      data: {
        flow_id: 12,
        flow_name: "Order Follow-up",
        customer_phone: "919876543211",
        phone_id: "123456789012345",
        conversation_id: null,
        status: "started",
        last_node_id: null,
        flow_executed: false,
        execution_summary: "Flow activation accepted. Orchestration continues in the background.",
        previous_flow_ended: true,
        previous_flow_ids: [8],
        trace_id: "api-act-1710000000000",
      },
    },
    codeExamples: {
      curl: `curl -X POST ${API_BASE}/api/v1/flows/12/activate \\
  -H "X-API-Key: pk_live_your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{
    "phone_number": "919876543210",
    "to": "919876543211",
    "variables": { "order_id": "ORD-1001" }
  }'`,
      javascript: `const response = await fetch("${API_BASE}/api/v1/flows/12/activate", {
  method: "POST",
  headers: {
    "X-API-Key": "pk_live_your_api_key_here",
    "Content-Type": "application/json",
  },
  body: JSON.stringify({
    phone_number: "919876543210",
    to: "919876543211",
    variables: { order_id: "ORD-1001" },
  }),
});
console.log(await response.json());`,
      python: `import requests

response = requests.post(
    "${API_BASE}/api/v1/flows/12/activate",
    headers={
        "X-API-Key": "pk_live_your_api_key_here",
        "Content-Type": "application/json",
    },
    json={
        "phone_number": "919876543210",
        "to": "919876543211",
        "variables": {"order_id": "ORD-1001"},
    },
)
print(response.json())`,
      nodejs: `const axios = require("axios");

const { data } = await axios.post(
  "${API_BASE}/api/v1/flows/12/activate",
  {
    phone_number: "919876543210",
    to: "919876543211",
    variables: { order_id: "ORD-1001" },
  },
  { headers: { "X-API-Key": "pk_live_your_api_key_here" } }
);
console.log(data);`,
    },
  },
};

export const ERROR_CODES = [
  {
    code: "INVALID_API_KEY",
    status: 401,
    message: "API key is missing, invalid, or has been revoked.",
    solution: "Check that you are passing the correct API key in the X-API-Key header. Generate a new key from the Ping dashboard if needed.",
  },
  {
    code: "RATE_LIMIT_EXCEEDED",
    status: 429,
    message: "Rate limit exceeded. Please slow down your requests.",
    solution: "Wait for the Retry-After duration specified in the response header. Upgrade your rate limit from the API Keys settings.",
  },
  {
    code: "PHONE_NUMBER_NOT_FOUND",
    status: 404,
    message: "The phone number is not linked to your account.",
    solution: "Ensure the phone_number you provided is connected to your Ping account. Check the Phone Numbers section in the dashboard.",
  },
  {
    code: "ACCESS_TOKEN_EXPIRED",
    status: 503,
    message: "WhatsApp access token has expired. Please reconnect your phone in the Ping dashboard.",
    solution: "Go to the Ping dashboard Phone Numbers section and reconnect your WhatsApp Business account.",
  },
  {
    code: "INVALID_PHONE_FORMAT",
    status: 400,
    message: "Recipient phone number format is invalid. Expected: country code + number (e.g. 919876543210).",
    solution: "Provide the recipient number with country code and without + or spaces. Example: 919876543210.",
  },
  {
    code: "INVALID_PAYLOAD",
    status: 400,
    message: "Request body is missing required fields.",
    solution: "Check the API documentation for required fields and ensure all are present in your request body.",
  },
  {
    code: "INVALID_MEDIA_URL",
    status: 400,
    message: "Media URL is invalid or unreachable.",
    solution: "Ensure the media_url is a valid, publicly accessible URL. The URL must be reachable from our servers.",
  },
  {
    code: "INVALID_MEDIA_TYPE",
    status: 400,
    message: "Unsupported media type. Allowed: image, video, audio, document.",
    solution: "Set the type field to one of: image, video, audio, document.",
  },
  {
    code: "TEMPLATE_NOT_FOUND",
    status: 404,
    message: "Template not found or not approved.",
    solution: "Verify the template_name exists and has been approved by Meta. Check the Templates section in the dashboard.",
  },
  {
    code: "META_API_ERROR",
    status: 502,
    message: "WhatsApp Cloud API returned an error.",
    solution: "This is an error from Meta. Check the error details in the response for more information. Common causes: invalid recipient, template variables mismatch.",
  },
  {
    code: "IP_NOT_WHITELISTED",
    status: 403,
    message: "Your IP address is not in the API key whitelist.",
    solution: "Add your server IP to the API key whitelist in the Ping dashboard, or remove the whitelist restriction.",
  },
  {
    code: "INSUFFICIENT_BALANCE",
    status: 402,
    message: "Insufficient wallet balance. Recharge to continue sending.",
    solution: "Recharge the prepaid wallet in the Ping dashboard. The error details include remaining balance, required amount, shortfall, and billing category. Session text/media usually cost ₹0 and still send.",
  },
  {
    code: "WALLET_FROZEN",
    status: 403,
    message: "Wallet is frozen. Contact support to re-enable sending.",
    solution: "Contact Ping support to unfreeze the wallet. Remaining balance is returned in error.details.",
  },
  {
    code: "INTERNAL_ERROR",
    status: 500,
    message: "An unexpected error occurred.",
    solution: "Retry the request. If the error persists, contact Ping support.",
  },
  {
    code: "FLOW_NOT_FOUND",
    status: 404,
    message: "Flow not found or not accessible with this API key.",
    solution: "Verify the flow ID exists in your account and is active.",
  },
  {
    code: "FLOW_NOT_API_TRIGGER",
    status: 409,
    message: "This flow is not configured with an API trigger.",
    solution: "Open the flow in the builder, set the trigger type to API Activation, and configure expected variables.",
  },
  {
    code: "FLOW_NOT_ALLOCATED",
    status: 409,
    message: "Flow is not allocated to the specified business phone number.",
    solution: "Allocate the flow to your WhatsApp phone in the Flows dashboard before activating via API.",
  },
  {
    code: "FLOW_VALIDATION_FAILED",
    status: 422,
    message: "Flow activation validation failed.",
    solution: "Check the error details for missing required variables, unknown variables, or invalid variable names.",
  },
  {
    code: "ACTIVE_SESSION_EXISTS",
    status: 409,
    message: "Customer already has an active flow session on this phone.",
    solution: "Flow activation automatically ends previous sessions. If you see this error, contact Ping support.",
  },
];

export const GETTING_STARTED = {
  title: "Getting Started with Ping API",
  subtitle: "Send your first WhatsApp message in under 5 minutes",
  steps: [
    {
      number: 1,
      title: "Create a Ping Account",
      description: "Sign up at ping.blutec.ai and complete your business profile setup.",
    },
    {
      number: 2,
      title: "Connect WhatsApp Number",
      description: "Go to Phone Numbers in the dashboard and connect your WhatsApp Business account via Meta Business Suite.",
    },
    {
      number: 3,
      title: "Generate an API Key",
      description: "Navigate to API Keys in the sidebar. Click \"Generate New Key\" and copy your key securely. You will only see the full key once.",
    },
    {
      number: 4,
      title: "Send Your First Message",
      description: "Use the API key to send a test message. Try the playground on the right, or use cURL:",
      code: `curl -X POST ${API_BASE}/api/v1/messages/text \\
  -H "X-API-Key: YOUR_API_KEY" \\
  -H "Content-Type: application/json" \\
  -d '{
    "phone_number": "YOUR_WA_NUMBER",
    "to": "RECIPIENT_NUMBER",
    "message": "Hello from Ping!"
  }'`,
    },
  ],
  baseUrl: API_BASE,
  rateLimit: "100 requests per minute per API key (configurable)",
  authentication: "All API requests require an X-API-Key header with a valid live API key (pk_live_...).",
};

export const AUTH_SECTION = {
  id: "api-key-usage",
  title: "Authentication",
  description: "All API requests to Ping must include your API key for authentication. Pass it via the X-API-Key header.",
  headerExample: 'X-API-Key: pk_live_your_api_key_here',
  notes: [
    "API keys are prefixed with pk_live_ for live environment.",
    "Store your API key securely. Never expose it in frontend code or public repositories.",
    "Each key has a configurable rate limit (default: 100 requests/minute).",
    "You can optionally configure an IP whitelist for additional security.",
    "Revoked keys will immediately stop working. Generate a new key if compromised.",
  ],
};

export const TEMPLATE_GUIDE = {
  title: "Template Sending Guide",
  subtitle: "Learn how to send WhatsApp templates with parameters, media, and buttons via the Ping API.",
  sections: [
    {
      id: "wallet-billing",
      title: "Wallet billing",
      description: "Template sends debit the prepaid INR wallet (same balance as the Ping dashboard). Call GET /api/v1/templates?status=APPROVED for the real template name and language, then GET /api/v1/wallet and GET /api/v1/wallet/prices before sending. Session text and media bill as service (usually ₹0). If the wallet cannot cover a template, the API returns HTTP 402 INSUFFICIENT_BALANCE with remaining, required, and shortfall. Frozen wallets return 403 WALLET_FROZEN. Successful sends include data.wallet with charged and remaining.",
      subsections: [],
    },
    {
      id: "param-formats",
      title: "Numbered vs Named Parameters",
      description: "WhatsApp templates support two parameter formats. The format is decided when the template is created and cannot be changed later.",
      subsections: [
        {
          title: "Numbered Parameters (default)",
          description: "Variables are numbered sequentially: {{1}}, {{2}}, {{3}}. When sending, you pass parameter values in the same order.",
          json: JSON.stringify({
            phone_number: "919876543210",
            to: "919876543211",
            template_name: "order_update",
            language: "en_US",
            components: [
              {
                type: "body",
                parameters: [
                  { type: "text", text: "John" },
                  { type: "text", text: "ORD12345" },
                  { type: "text", text: "March 10, 2026" },
                ],
              },
            ],
          }, null, 2),
          note: "The first parameter replaces {{1}}, the second replaces {{2}}, and so on.",
        },
        {
          title: "Named Parameters",
          description: "Variables use descriptive names: {{customer_name}}, {{order_id}}. The sending payload is identical — parameters are still passed in order. Meta maps them by position, not by name.",
          json: JSON.stringify({
            phone_number: "919876543210",
            to: "919876543211",
            template_name: "welcome_named",
            language: "en_US",
            components: [
              {
                type: "body",
                parameters: [
                  { type: "text", text: "John" },
                  { type: "text", text: "WELCOME20" },
                ],
              },
            ],
          }, null, 2),
          note: "Even with named parameters, the API payload uses positional order — the first parameter fills the first variable in the template body.",
        },
      ],
    },
    {
      id: "header-vars",
      title: "Header Parameters",
      description: "Templates can have a header with text variables or media (image, video, document).",
      subsections: [
        {
          title: "Text Header with Variable",
          description: "If the header has a variable (e.g., \"Hello {{1}}\"), pass it in a header component.",
          json: JSON.stringify({
            phone_number: "919876543210",
            to: "919876543211",
            template_name: "greeting_template",
            language: "en_US",
            components: [
              {
                type: "header",
                parameters: [
                  { type: "text", text: "John" },
                ],
              },
              {
                type: "body",
                parameters: [
                  { type: "text", text: "ORD12345" },
                ],
              },
            ],
          }, null, 2),
        },
        {
          title: "Image Header",
          description: "For templates with an image header, provide a public image URL.",
          json: JSON.stringify({
            phone_number: "919876543210",
            to: "919876543211",
            template_name: "promo_with_image",
            language: "en_US",
            components: [
              {
                type: "header",
                parameters: [
                  { type: "image", image: { link: "https://example.com/promo-banner.jpg" } },
                ],
              },
              {
                type: "body",
                parameters: [
                  { type: "text", text: "50%" },
                ],
              },
            ],
          }, null, 2),
        },
        {
          title: "Video Header",
          description: "For templates with a video header, provide a public video URL.",
          json: JSON.stringify({
            phone_number: "919876543210",
            to: "919876543211",
            template_name: "video_announcement",
            language: "en_US",
            components: [
              {
                type: "header",
                parameters: [
                  { type: "video", video: { link: "https://example.com/video.mp4" } },
                ],
              },
            ],
          }, null, 2),
        },
        {
          title: "Document Header",
          description: "For templates with a document header, provide a public document URL and optional filename.",
          json: JSON.stringify({
            phone_number: "919876543210",
            to: "919876543211",
            template_name: "invoice_template",
            language: "en_US",
            components: [
              {
                type: "header",
                parameters: [
                  { type: "document", document: { link: "https://example.com/invoice.pdf", filename: "Invoice_March.pdf" } },
                ],
              },
            ],
          }, null, 2),
        },
      ],
    },
    {
      id: "button-vars",
      title: "Button Parameters",
      description: "Some template buttons have dynamic values that must be provided at send time.",
      subsections: [
        {
          title: "Dynamic URL Button",
          description: "If a URL button contains {{1}} (e.g., https://example.com/track/{{1}}), you must pass the dynamic suffix. Use the button index (0-based) matching the button order in the template.",
          json: JSON.stringify({
            phone_number: "919876543210",
            to: "919876543211",
            template_name: "order_tracking",
            language: "en_US",
            components: [
              {
                type: "body",
                parameters: [
                  { type: "text", text: "John" },
                  { type: "text", text: "ORD12345" },
                ],
              },
              {
                type: "button",
                sub_type: "url",
                index: "0",
                parameters: [
                  { type: "text", text: "ORD12345" },
                ],
              },
            ],
          }, null, 2),
          note: "The index refers to the button position (0-based) in the template's buttons array. Only URL buttons with a dynamic {{1}} suffix require parameters.",
        },
        {
          title: "Copy Code Button (Offer Code / Coupon)",
          description: "Templates with a \"Copy Offer Code\" button need a coupon_code parameter at send time.",
          json: JSON.stringify({
            phone_number: "919876543210",
            to: "919876543211",
            template_name: "flash_sale",
            language: "en_US",
            components: [
              {
                type: "body",
                parameters: [
                  { type: "text", text: "50%" },
                ],
              },
              {
                type: "button",
                sub_type: "copy_code",
                index: "0",
                parameters: [
                  { type: "coupon_code", coupon_code: "SAVE50" },
                ],
              },
            ],
          }, null, 2),
          note: "The coupon_code type is specific to Copy Code buttons. The index should match the button position in the template.",
        },
      ],
    },
    {
      id: "auth-templates",
      title: "Authentication Templates (OTP)",
      description: "Authentication templates are used for one-time passwords (OTP). They have a fixed body set by Meta and an OTP Copy Code button. When sending, pass the OTP code as a button parameter.",
      subsections: [
        {
          title: "Sending an OTP Template",
          description: "Authentication templates do not have user-defined body text. The body is auto-generated by Meta. You only need to pass the OTP code for the button.",
          json: JSON.stringify({
            phone_number: "919876543210",
            to: "919876543211",
            template_name: "auth_otp",
            language: "en_US",
            components: [
              {
                type: "body",
                parameters: [
                  { type: "text", text: "123456" },
                ],
              },
              {
                type: "button",
                sub_type: "url",
                index: "0",
                parameters: [
                  { type: "text", text: "123456" },
                ],
              },
            ],
          }, null, 2),
          note: "The body parameter contains the OTP code that appears in the message. The button parameter fills the copy-code or autofill URL value.",
        },
      ],
    },
    {
      id: "complete-examples",
      title: "Complete Examples",
      description: "Full request payloads for common template scenarios.",
      subsections: [
        {
          title: "Marketing Template with Image + URL Button (Numbered)",
          description: "A marketing template with an image header, body variables, and a dynamic URL button.",
          json: JSON.stringify({
            phone_number: "919876543210",
            to: "919876543211",
            template_name: "seasonal_promo",
            language: "en_US",
            components: [
              {
                type: "header",
                parameters: [
                  { type: "image", image: { link: "https://example.com/sale-banner.jpg" } },
                ],
              },
              {
                type: "body",
                parameters: [
                  { type: "text", text: "John" },
                  { type: "text", text: "40%" },
                  { type: "text", text: "March 15" },
                ],
              },
              {
                type: "button",
                sub_type: "url",
                index: "0",
                parameters: [
                  { type: "text", text: "spring-2026" },
                ],
              },
            ],
          }, null, 2),
        },
        {
          title: "Utility Template with Named Parameters + Copy Code",
          description: "A utility template using named parameters with a copy code offer button.",
          json: JSON.stringify({
            phone_number: "919876543210",
            to: "919876543211",
            template_name: "order_confirmation_named",
            language: "en_US",
            components: [
              {
                type: "body",
                parameters: [
                  { type: "text", text: "John" },
                  { type: "text", text: "ORD12345" },
                  { type: "text", text: "March 12, 2026" },
                ],
              },
              {
                type: "button",
                sub_type: "copy_code",
                index: "0",
                parameters: [
                  { type: "coupon_code", coupon_code: "THANKYOU20" },
                ],
              },
            ],
          }, null, 2),
          note: "Even for named parameter templates, the sending payload passes values in positional order.",
        },
        {
          title: "Simple Text-Only Template (No Variables)",
          description: "Some templates have no variables at all. Just provide the template name and language.",
          json: JSON.stringify({
            phone_number: "919876543210",
            to: "919876543211",
            template_name: "welcome_message",
            language: "en_US",
          }, null, 2),
          note: "If a template has no variables, the components array can be omitted entirely.",
        },
      ],
    },
  ],
};
